/**
 * Global Error Boundary
 * Catches React errors and displays fallback UI
 */

import { Component, type ReactNode } from 'react';
import { config } from '@config/env';
import { useAuthStore } from '@/stores/authStore';
import { debugWarn, logError } from '@/utils/logger';

interface ClientErrorPayload {
  message: string;
  stack?: string;
  component_stack?: string;
  url?: string;
  route?: string;
  user_agent?: string;
  timestamp?: string;
  metadata?: Record<string, unknown>;
}

const reportClientError = async (payload: ClientErrorPayload) => {
  if (!config.isProduction) {
    return;
  }

  try {
    const token = useAuthStore.getState().token;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    await fetch(`${config.apiUrl}/api/v1/client-errors`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    if (config.isDevelopment) {
      debugWarn('Client error reporting failed:', err);
    }
  }
};

interface Props {
  children: ReactNode;
  fallback?: (error: Error, retry: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      logError('❌ ErrorBoundary caught an error:', error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });

    const now = new Date().toISOString();
    const url = typeof window !== 'undefined' ? window.location.href : undefined;
    const route = typeof window !== 'undefined' ? window.location.pathname : undefined;
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

    void reportClientError({
      message: error.message || 'Unknown error',
      stack: error.stack,
      component_stack: errorInfo.componentStack || undefined,
      url,
      route,
      user_agent: userAgent,
      timestamp: now,
      metadata: {
        component: 'ErrorBoundary',
      },
    });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-recessed px-4">
          <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-8">
            <div className="text-center">
              {/* Error Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-status-failed-surface mb-4">
                <svg
                  className="h-8 w-8 text-status-failed"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              {/* Error Title */}
              <h1 className="text-2xl font-bold text-ink-strong mb-2">
                Oops! Something went wrong
              </h1>

              {/* Error Message */}
              <p className="text-ink mb-6">
                {this.state.error.message || 'An unexpected error occurred'}
              </p>

              {/* Error Details (Development Only) */}
              {import.meta.env.DEV && this.state.errorInfo && (
                <details className="text-left mb-6">
                  <summary className="cursor-pointer text-sm text-ink-muted mb-2">
                    Technical Details
                  </summary>
                  <pre className="text-xs bg-surface-recessed p-4 rounded overflow-auto max-h-48">
                    {this.state.error.stack}
                    {'\n\n'}
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="px-6 py-2 bg-accent text-on-accent rounded-lg hover:bg-accent-hover transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.location.href = '/devices'}
                  className="px-6 py-2 bg-border-subtle text-ink rounded-lg hover:bg-border-strong transition-colors"
                >
                  Back to Ops
                </button>
              </div>

              {/* Help Text */}
              <p className="mt-6 text-sm text-ink-muted">
                If this problem persists, please contact support.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
